import { api } from '@mbscan/client-api'
import { isSome } from '@mbscan/shared'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo } from 'react'

import { CenteredScreen } from '@/components/layout/centered-screen'
import { TenantSelectionScreen } from '@/components/screens/tenant-selection-screen'
import { ErrorRecoveryScreen } from '@/components/system-screens'
import { classifyError } from '@/features/system/error-recovery'
import type { Tenant } from '@/features/tenant/types'
import { AuthenticatedApolloProvider } from '@/providers/authenticated-apollo-provider'

const LAST_TENANT_KEY = 'mill-box:last-tenant'

function TenantSelectionContent() {
  const { data, error, loading } = api.useFetchMyIdentityQuery()
  const navigate = useNavigate()
  const tenants: Tenant[] = useMemo(
    () =>
      (data?.myIdentity.users ?? []).map(({ tenant }) => ({
        id: tenant.id,
        name: tenant.name,
        company: tenant.companyName,
        location: tenant.prefecture,
      })),
    [data],
  )

  useEffect(() => {
    const onlyTenant = tenants.length === 1 ? tenants[0] : undefined
    if (isSome(onlyTenant)) {
      window.localStorage.setItem(LAST_TENANT_KEY, onlyTenant.id)
      void navigate({ to: '/t/$tenant_id', params: { tenant_id: onlyTenant.id }, replace: true })
    }
  }, [navigate, tenants])

  if (loading || tenants.length === 1) {
    return <CenteredScreen aria-label="現場を読み込み中" />
  }
  if (isSome(error)) {
    return (
      <ErrorRecoveryScreen
        kind={classifyError(error)}
        message={error.message}
        onHome={() => {
          window.location.assign('/')
        }}
        onReload={() => {
          window.location.reload()
        }}
        onSignOut={() => void navigate({ to: '/sign_out', replace: true })}
      />
    )
  }

  return (
    <TenantSelectionScreen
      tenants={tenants}
      recentTenantId={window.localStorage.getItem(LAST_TENANT_KEY)}
      onSelect={(tenant) => {
        window.localStorage.setItem(LAST_TENANT_KEY, tenant.id)
        void navigate({ to: '/t/$tenant_id', params: { tenant_id: tenant.id } })
      }}
      onLogout={() => {
        void navigate({ to: '/sign_out' })
      }}
    />
  )
}

function TenantSelectionPage() {
  return (
    <AuthenticatedApolloProvider tenantId={null}>
      <TenantSelectionContent />
    </AuthenticatedApolloProvider>
  )
}

export const Route = createFileRoute('/')({ component: TenantSelectionPage })
